import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import "./MagAbsorbStory.css";
import a0 from "@/assets/mag-absorb-story/aquamin-marine.webp.asset.json";
import a1 from "@/assets/mag-absorb-story/blue-studio-closing.webp.asset.json";
import a2 from "@/assets/mag-absorb-story/magnesium-aspartate.webp.asset.json";
import a3 from "@/assets/mag-absorb-story/magnesium-chloride.webp.asset.json";
import a4 from "@/assets/mag-absorb-story/magnesium-glass-sculpture.webp.asset.json";
import a5 from "@/assets/mag-absorb-story/magnesium-gluconate.webp.asset.json";

export interface MagAbsorbStoryProps {
  headingClassName: string;
  onChooseSupply: () => void;
  stickyTop?: number;
  part?: "formula" | "closing" | "all";
  offerDetails?: ReactNode;
}

const ASSET_URLS: Record<string, string> = {"aquamin-marine.webp": a0.url,"blue-studio-closing.webp": a1.url,"magnesium-aspartate.webp": a2.url,"magnesium-chloride.webp": a3.url,"magnesium-glass-sculpture.webp": a4.url,"magnesium-gluconate.webp": a5.url};
const asset = (name: string) => ASSET_URLS[name] ?? "";
const FORMS = [
  {
    shortName: "Bisglycinate", subtitle: "Liposomal form", amount: 200,
    image: "magnesium-glass-sculpture.webp", alt: "Layered cobalt glass with soft blue reflections",
    caption: "Liposomal bisglycinate", kicker: "LIPOSOMAL FORM", name: "Magnesium bisglycinate",
    copy: "Magnesium paired with glycine, in a liposomal form. 200 mg in every two-tablet serving.",
  },
  {
    shortName: "Aquamin®", subtitle: "Marine-sourced magnesium", amount: 100,
    image: "aquamin-marine.webp", alt: "Clear seawater flowing over pale mineral stone",
    caption: "Aquamin® marine magnesium", kicker: "MARINE-SOURCED", name: "Aquamin® marine magnesium",
    copy: "Marine-sourced magnesium. 100 mg in each daily serving, alongside four other magnesium forms.",
  },
  {
    shortName: "Chloride", subtitle: "Magnesium salt", amount: 60,
    image: "magnesium-chloride.webp", alt: "Translucent mineral crystals on a blue glass surface",
    caption: "Magnesium chloride", kicker: "MINERAL SALT", name: "Magnesium chloride",
    copy: "60 mg of magnesium chloride in your daily formula, alongside four other magnesium forms.",
  },
  {
    shortName: "Aspartate", subtitle: "Amino-acid salt", amount: 50,
    image: "magnesium-aspartate.webp", alt: "Fine white powder with delicate ridges on pale blue ceramic",
    caption: "Magnesium aspartate", kicker: "AMINO-ACID SALT", name: "Magnesium aspartate",
    copy: "Magnesium paired with aspartic acid. 50 mg per daily serving, built into the same two-tablet routine.",
  },
  {
    shortName: "Gluconate", subtitle: "Organic salt", amount: 30,
    image: "magnesium-gluconate.webp", alt: "White powder in a glass dish with a clear glass spatula",
    caption: "Magnesium gluconate", kicker: "ORGANIC SALT", name: "Magnesium gluconate",
    copy: "Magnesium paired with gluconic acid. 30 mg completes the five-form blend, with B6, D3, zinc and copper in the same serving.",
  },
] as const;

function ArrowIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 12h15M13 5l7 7-7 7" /></svg>;
}

export default function MagAbsorbStory({ headingClassName, onChooseSupply, stickyTop = 144, part = "all", offerDetails }: MagAbsorbStoryProps) {
  const uid = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const artRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeRef = useRef(0);
  const jumpRef = useRef<((index: number) => void) | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [horizontal, setHorizontal] = useState(false);
  const safeStickyTop = Number.isFinite(stickyTop) ? Math.max(0, stickyTop) : 144;
  const activeForm = FORMS[activeIndex];
  const panelId = `${uid}-ingredient-panel`;
  const captionId = `${uid}-ingredient-caption`;
  const tabId = (index: number) => `${uid}-ingredient-${index}`;
  const rootStyle = { "--mag-story-sticky-top": `${safeStickyTop}px` } as CSSProperties;

  const selectForm = useCallback((index: number, focus = false) => {
    if (index < 0 || index >= FORMS.length) return;
    activeRef.current = index;
    setActiveIndex(index);
    if (focus) tabRefs.current[index]?.focus({ preventScroll: true });
    jumpRef.current?.(index);
  }, []);

  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number | undefined;
    if ((horizontal && event.key === "ArrowRight") || (!horizontal && event.key === "ArrowDown")) next = (index + 1) % FORMS.length;
    if ((horizontal && event.key === "ArrowLeft") || (!horizontal && event.key === "ArrowUp")) next = (index + FORMS.length - 1) % FORMS.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = FORMS.length - 1;
    if (next !== undefined) {
      event.preventDefault();
      selectForm(next, true);
    }
  };

  useEffect(() => {
    const root = rootRef.current;
    const track = trackRef.current;
    const stage = stageRef.current;
    const art = artRef.current;
    if (!root || !track || !stage || !art) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobile = window.matchMedia("(max-width: 760px)");
    let frame = 0;
    let disposed = false;
    let needsMeasure = true;
    let stageHeight = 0;
    let viewportHeight = window.innerHeight;
    let pinEnabled = true;
    let pendingJump: number | null = null;
    let jumpTarget: number | null = null;
    let jumpExpires = 0;
    let lastArtOffset = "";
    const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

    const schedule = () => {
      if (!disposed && !frame) frame = window.requestAnimationFrame(update);
    };
    const resize = () => { needsMeasure = true; schedule(); };
    const cancelJump = () => {
      if (jumpTarget !== null) { jumpTarget = null; schedule(); }
    };
    const cancelJumpFromKeyboard = (event: KeyboardEvent) => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) cancelJump();
    };

    const update = () => {
      frame = 0;
      if (disposed) return;
      if (needsMeasure) {
        viewportHeight = window.innerHeight;
        setHorizontal(mobile.matches);
        // Keep the image and controls readable in short windows and landscape.
        const bottomSpace = mobile.matches ? 96 : 28;
        pinEnabled = viewportHeight - safeStickyTop - bottomSpace >= (mobile.matches ? 430 : 470);
        root.dataset.scrollEnabled = String(pinEnabled);
        stageHeight = stage.getBoundingClientRect().height;
        needsMeasure = false;
      }
      const bounds = track.getBoundingClientRect();
      const travel = Math.max(0, bounds.height - stageHeight);
      const progress = travel > 1 ? clamp((safeStickyTop - bounds.top) / travel, 0, 1) : 0;
      const artOffset = reducedMotion.matches || !pinEnabled ? "0px" : `${((progress - 0.5) * -12).toFixed(2)}px`;
      if (artOffset !== lastArtOffset) {
        art.style.setProperty("--mag-story-art-y", artOffset);
        lastArtOffset = artOffset;
      }

      if (!pinEnabled || travel <= 1) {
        pendingJump = null;
        jumpTarget = null;
        // Native document flow retains the manually selected form.
        return;
      }
      if (pendingJump !== null) {
        const index = pendingJump;
        pendingJump = null;
        const start = window.scrollY + bounds.top - safeStickyTop;
        const maximumScroll = Math.max(0, document.documentElement.scrollHeight - viewportHeight);
        const destination = clamp(start + travel * (index + 0.5) / FORMS.length, 0, maximumScroll);
        jumpTarget = index;
        jumpExpires = performance.now() + 2200;
        window.scrollTo({ top: destination, behavior: reducedMotion.matches ? "instant" : "smooth" });
        if (Math.abs(window.scrollY - destination) < 1) { jumpTarget = null; schedule(); }
        return;
      }
      const index = Math.min(FORMS.length - 1, Math.floor(progress * FORMS.length));
      if (jumpTarget !== null) {
        if (index !== jumpTarget && performance.now() < jumpExpires) return;
        jumpTarget = null;
      }
      if (index !== activeRef.current) {
        activeRef.current = index;
        setActiveIndex(index);
      }
    };

    jumpRef.current = (index) => { pendingJump = index; schedule(); };
    const observer = new ResizeObserver(resize);
    observer.observe(track);
    observer.observe(stage);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", resize, { passive: true });
    window.addEventListener("wheel", cancelJump, { passive: true });
    window.addEventListener("touchstart", cancelJump, { passive: true });
    window.addEventListener("pointerdown", cancelJump, { passive: true });
    window.addEventListener("keydown", cancelJumpFromKeyboard);
    window.addEventListener("scrollend", cancelJump, { passive: true });
    window.visualViewport?.addEventListener("resize", resize, { passive: true });
    reducedMotion.addEventListener("change", schedule);
    mobile.addEventListener("change", resize);
    void document.fonts?.ready.then(() => { if (!disposed) resize(); });
    schedule();

    return () => {
      disposed = true;
      jumpRef.current = null;
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", resize);
      window.removeEventListener("wheel", cancelJump);
      window.removeEventListener("touchstart", cancelJump);
      window.removeEventListener("pointerdown", cancelJump);
      window.removeEventListener("keydown", cancelJumpFromKeyboard);
      window.removeEventListener("scrollend", cancelJump);
      window.visualViewport?.removeEventListener("resize", resize);
      reducedMotion.removeEventListener("change", schedule);
      mobile.removeEventListener("change", resize);
    };
  }, [safeStickyTop, part]);

  return (
    <div ref={rootRef} className="mag-story" style={rootStyle}>
      {part !== "closing" && <>
      <section id="mag-story-ingredients" className="mag-story__ingredients" aria-labelledby={`${uid}-ingredients-heading`}>
        <div className="mag-story__wrap">
          <div className="mag-story__editorial-head">
            <div>
              <p className="mag-story__eyebrow">THE MAG ABSORB PRO DIFFERENCE</p>
              <h2 id={`${uid}-ingredients-heading`} className={`${headingClassName} mag-story__heading mag-story__heading--gradient`}>Five magnesium forms.<br />One nightly reset.</h2>
            </div>
            <p>Liposomal bisglycinate and Aquamin® marine magnesium, joined by chloride, aspartate and gluconate. Sleep, relaxation and muscle recovery support, in one formula.</p>
          </div>
          <div ref={trackRef} className="mag-story__track">
            <div ref={stageRef} className="mag-story__stage">
              <div className="mag-story__grid">
                <figure ref={artRef} className="mag-story__art" aria-labelledby={captionId}>
                  {FORMS.map((form, index) => (
                    <img key={form.image} src={asset(form.image)} alt={index === activeIndex ? form.alt : ""} aria-hidden={index !== activeIndex} className={`mag-story__art-image${index === activeIndex ? " is-active" : ""}`} width={1254} height={1254} loading="lazy" decoding="async" />
                  ))}
                  <div className="mag-story__art-overline" aria-hidden="true"><span>TRUWE</span><span>PRECISION FORMULATED</span></div>
                  <figcaption id={captionId} className="mag-story__art-caption">{activeForm.caption}</figcaption>
                </figure>
                <div className="mag-story__selection">
                  <h3 className="mag-story__selection-heading">Five forms. One formula.</h3>
                  <div className="mag-story__list-labels" aria-hidden="true"><span>FIVE MAGNESIUM FORMS</span><span>PER 2 TABLETS</span></div>
                  <div className="mag-story__tabs" role="tablist" aria-label="Explore the magnesium forms" aria-orientation={horizontal ? "horizontal" : "vertical"}>
                    {FORMS.map((form, index) => (
                      <button key={form.image} ref={(element) => { tabRefs.current[index] = element; }} type="button" id={tabId(index)} role="tab" aria-controls={panelId} aria-selected={activeIndex === index} tabIndex={activeIndex === index ? 0 : -1} onClick={() => selectForm(index)} onKeyDown={(event) => onTabKeyDown(event, index)}>
                        <img className="mag-story__thumb" src={asset(form.image)} alt="" width={64} height={64} loading="lazy" decoding="async" />
                        <span className="mag-story__form-name">{form.shortName}<small>{form.subtitle}</small></span>
                        <span className="mag-story__amount">{form.amount} <small>mg</small></span>
                        <ArrowIcon />
                      </button>
                    ))}
                  </div>
                  <div id={panelId} className="mag-story__description" role="tabpanel" tabIndex={0} aria-labelledby={tabId(activeIndex)}>
                    <span className="mag-story__eyebrow">{activeForm.kicker}</span>
                    <div className="mag-story__panel-heading"><h3>{activeForm.name}</h3><span className="mag-story__mobile-dose">{activeForm.amount} <small>mg</small></span></div>
                    <p>{activeForm.copy}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="mag-story-cofactors" className="mag-story__cofactors mag-story__wrap" aria-labelledby={`${uid}-cofactors-heading`}>
        <div className="mag-story__cofactors-copy">
          <p className="mag-story__eyebrow">BEYOND MAGNESIUM</p>
          <h2 id={`${uid}-cofactors-heading`} className={`${headingClassName} mag-story__heading mag-story__heading--gradient`}>More than magnesium.<br />Already included.</h2>
          <p>Active B6, vitamin D3, zinc and copper complement the five-form magnesium blend. All in the same two-tablet serving.</p>
        </div>
        <div className="mag-story__cofactor-card">
          <p className="mag-story__eyebrow">PER TWO-TABLET SERVING</p>
          <dl>
            <div><dt>Zinc + copper bisglycinate</dt><dd>12.5 mg</dd></div>
            <div><dt>Active vitamin B6 <span>P-5-P</span></dt><dd>3 mg</dd></div>
            <div><dt>Vitamin D3</dt><dd>600 IU</dd></div>
          </dl>
          <div className="mag-story__matrix"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 3C7 2 2 9 5 15s14 5 15-12ZM4 21 15 9" /></svg><div><strong>Bio-Absorb Matrix</strong><p>Also includes a proprietary phytonutrient blend.</p></div></div>
        </div>
      </section>

      </>}
      {part !== "formula" &&
      <section id="mag-story-nightly-reset" className="mag-story__closing" aria-labelledby={`${uid}-closing-heading`}>
        <img src={asset("blue-studio-closing.webp")} alt="Mag Absorb Pro canister in a refined blue studio with a glass halo" width={1254} height={1254} loading="lazy" decoding="async" />
        <div className="mag-story__closing-copy">
          <p className="mag-story__eyebrow">WITH MAG ABSORB PRO</p>
          <h2 id={`${uid}-closing-heading`} className="mag-story__heading mag-story__closing-heading">Start your<br />nightly reset.</h2>
          <p className="mag-story__closing-deck">Five magnesium forms. No melatonin.<br />Sleep &amp; recovery support, every day.</p>
          {offerDetails && <div className="mag-story__offer-details">{offerDetails}</div>}
          <button type="button" className="mag-story__supply-button" onClick={onChooseSupply}>Choose my supply <ArrowIcon /></button>
          <p className="mag-story__closing-footnote">Food supplement. Not a medicine.</p>
        </div>
      </section>
      }
    </div>
  );
}
